import { animalSpeciesHelper } from '@farmshare/utils';
import {
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { sortBy } from 'lodash';
import moment from 'moment';
import { useMemo, useRef, useState } from 'react';

import { STATUS_DISPLAY } from '../shared/styles';
import { BuyerSplitSlot } from '../shared/split-slot/buyer-split-slot';

import type { BuyerOrderItem, BuyerSlotInfo } from './use-buyer-orders';

/** Fixed height for the tab row so cards align whether they have tabs or not */
const TAB_ROW_HEIGHT = 40;

interface BuyerOrderCardProps {
  item: BuyerOrderItem;
  generatingPdf: boolean;
  completed?: boolean;
  onFillCutsheet: (item: BuyerOrderItem, slot: BuyerSlotInfo) => void;
  onEditCutsheet: (item: BuyerOrderItem, slot: BuyerSlotInfo) => void;
  onDownloadPdf: (item: BuyerOrderItem, slot: BuyerSlotInfo) => void;
}

export function BuyerOrderCard({
  item,
  generatingPdf,
  completed = false,
  onFillCutsheet,
  onEditCutsheet,
  onDownloadPdf,
}: BuyerOrderCardProps) {
  const species = animalSpeciesHelper(item.animalSpecies).label;
  const hasMultipleSlots = item.slots.length > 1;

  // Sort slots: missing cutsheets first, then by original order
  const sortedSlots = useMemo(
    () =>
      hasMultipleSlots
        ? sortBy(item.slots, (s) => (s.hasCutsheet ? 1 : 0))
        : item.slots,
    [item.slots, hasMultipleSlots],
  );

  const [activeTab, setActiveTab] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  const activeSlot = hasMultipleSlots ? sortedSlots[activeTab] : item.slots[0];

  const handleScrollRight = () => {
    scrollRef.current?.scrollBy({ left: 120, behavior: 'smooth' });
  };

  const handleScrollLeft = () => {
    scrollRef.current?.scrollBy({ left: -120, behavior: 'smooth' });
  };

  if (!activeSlot) return null;

  const slotReady = activeSlot.hasCutsheet;
  const borderColor = completed
    ? undefined
    : slotReady
      ? 'success.main'
      : 'error.main';

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2,
        bgcolor: 'background.paper',
        overflow: 'hidden',
        ...(borderColor
          ? {
              borderLeftWidth: 4,
              borderLeftStyle: 'solid',
              borderLeftColor: borderColor,
            }
          : {}),
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <CardContent
        sx={{
          p: 2,
          '&:last-child': { pb: 2 },
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
        }}
      >
        {/* Header: producer, processor, species, date */}
        <Stack sx={{ mb: 1.5 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
          >
            <Typography variant="h6" noWrap sx={{ minWidth: 0 }}>
              {item.producerName || 'Producer'}
            </Typography>
            <Stack alignItems="flex-end" sx={{ flexShrink: 0, ml: 1 }}>
              {completed && item.job.status && (
                <Typography variant="caption" color="text.disabled">
                  {STATUS_DISPLAY[item.job.status] ?? item.job.status}
                </Typography>
              )}
              {item.displayDate && (
                <Typography variant="caption" color="text.disabled">
                  Harvest Date:{' '}
                  {moment(item.displayDate).utc().format('MM/DD/YYYY')}
                </Typography>
              )}
              {item.job.jobId && (
                <Typography variant="caption" color="text.disabled">
                  Job ID: {item.job.jobId}
                </Typography>
              )}
            </Stack>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {item.processorName}
            {species ? ` · ${species}` : ''}
          </Typography>
        </Stack>

        {/* Tab row — always takes up space for alignment across cards */}
        <Box sx={{ minHeight: TAB_ROW_HEIGHT, mb: 1.5 }}>
          {hasMultipleSlots && (
            <Stack
              direction="row"
              alignItems="center"
              sx={{ height: TAB_ROW_HEIGHT }}
            >
              <Box
                ref={scrollRef}
                sx={{
                  display: 'flex',
                  gap: 1,
                  overflowX: 'hidden',
                  flex: 1,
                  scrollBehavior: 'smooth',
                }}
              >
                {sortedSlots.map((slot, idx) => {
                  const isActive = idx === activeTab;
                  const needsAttention = !slot.hasCutsheet;

                  return (
                    <Button
                      key={slot.cutsheetInfoId}
                      size="small"
                      variant={isActive ? 'contained' : 'outlined'}
                      onClick={() => setActiveTab(idx)}
                      sx={{
                        minWidth: 'auto',
                        px: 2,
                        py: 0.5,
                        flexShrink: 0,
                        whiteSpace: 'nowrap',
                        textTransform: 'none',
                        fontWeight: isActive ? 600 : 400,
                        ...(needsAttention && !isActive
                          ? {
                              borderColor: 'error.main',
                              color: 'error.main',
                            }
                          : {}),
                        ...(needsAttention && isActive
                          ? {
                              bgcolor: 'error.main',
                              '&:hover': { bgcolor: 'error.dark' },
                            }
                          : {}),
                      }}
                    >
                      {slot.splitLabel}
                    </Button>
                  );
                })}
              </Box>
              {sortedSlots.length > 2 && (
                <Stack direction="row" sx={{ ml: 0.5, flexShrink: 0 }}>
                  <Box
                    onClick={handleScrollLeft}
                    sx={{
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      color: 'text.secondary',
                      '&:hover': { color: 'text.primary' },
                    }}
                  >
                    <ChevronLeft fontSize="small" />
                  </Box>
                  <Box
                    onClick={handleScrollRight}
                    sx={{
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      color: 'text.secondary',
                      '&:hover': { color: 'text.primary' },
                    }}
                  >
                    <ChevronRight fontSize="small" />
                  </Box>
                </Stack>
              )}
            </Stack>
          )}
        </Box>

        {/* Active slot content — flex-grow pushes actions to bottom */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <BuyerSplitSlot
            label={activeSlot.splitLabel}
            existingBuyer={activeSlot.raw}
            readOnly={completed}
            cutsheetName={activeSlot.cutsheetName}
            isQuestionnaireResponse={!!activeSlot.questionnaireResponseId}
            isLocked={activeSlot.locked}
            processorName={item.processorName || undefined}
            downloadingPdf={generatingPdf}
            onFillCutsheet={() => onFillCutsheet(item, activeSlot)}
            onEditCutsheet={() => onEditCutsheet(item, activeSlot)}
            onDownloadPdf={() => onDownloadPdf(item, activeSlot)}
          />
        </Box>
      </CardContent>
    </Card>
  );
}
